import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { REF_KEY } from "@/components/PromoCode";
import { SiteFooter } from "@/pages/Policy";
import { motion } from "framer-motion";
import { ArrowRight, ScanEye, Sparkles, LayoutTemplate, Copy, Truck, ShieldCheck, Printer } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CaricaturePlans } from "@/pages/Pricing";
import Nav from "@/components/Nav";
import SampleBook, { SAMPLE_BOOKS } from "@/components/SampleBook";
import { SIZES, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IMG = {
  hero: "https://images.unsplash.com/photo-1676883344224-2fa5de6025db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  wedding: "https://images.unsplash.com/photo-1630526720753-aa4e71acf67d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  tradition: "https://images.unsplash.com/photo-1610173827002-62c0f1f05d04?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  family: "https://images.unsplash.com/photo-1661274766834-4a700d13dabb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  baby: "https://images.unsplash.com/photo-1774641373950-e674eb327011?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  travel: "https://images.unsplash.com/photo-1645497265285-17096c1a429e?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const STEPS = [
  { icon: ScanEye, t: "Auto-fix", d: "Small or slightly blurry photos are upscaled and sharpened for print automatically — nothing gets in your way." },
  { icon: Copy, t: "Smart curation", d: "Near-identical and shaky shots are set aside. Your highlights rise to the top." },
  { icon: LayoutTemplate, t: "Story layout", d: "Pages, sizes, a title page and warm captions — arranged around your occasion." },
  { icon: Sparkles, t: "Light enhance", d: "Gentle exposure, colour and sharpness correction. One switch to turn it off." },
];

const CARICATURE_SAMPLES = [
  ["/samples/rohan.jpg", "Rohan", "Single · cricket, cats, chai & leg day"],
  ["/samples/couple.jpg", "Simran & Arjun", "Couple · filter coffee, treks & garba"],
  ["/samples/family.jpg", "The Sharmas", "Family · Bruno the retriever, two cats & cricket"],
];

const OCCASIONS = [
  ["Wedding Memories", IMG.tradition],
  ["Baby Memories", IMG.baby],
  ["Travel stories", IMG.travel],
  ["Family milestones", IMG.family],
];

export default function Landing() {
  const { user } = useAuth();
  const [sample, setSample] = useState("wedding");
  const [params] = useSearchParams();
  const ref = params.get("ref");
  useEffect(() => { if (ref) localStorage.setItem(REF_KEY, ref.toUpperCase()); }, [ref]);
  const start = user ? "/new" : "/register";
  return (
    <div className="grain min-h-screen">
      <Nav />
      {ref && (
        <div className="bg-[#3B5BDB] px-5 py-2.5 text-center text-sm text-white" data-testid="referral-banner">
          A friend sent you here — <strong>₹100 off</strong> your first order with code <span className="font-mono font-bold tracking-wider">{ref.toUpperCase()}</span>. It's applied automatically at checkout.
        </div>
      )}
      <section className="mx-auto grid max-w-7xl gap-12 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-12 lg:pt-20">
        <div className="lg:col-span-5 rise">
          <p className="overline">Memories — Global or Desi, big or small deserve to be preserved in print forever.</p>
          <h1 className="mt-5 font-serif text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Your memories,<br /><em className="text-[#3B5BDB]">bound beautifully.</em>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#5E5868] sm:text-lg">
            Upload up to 100 photos, sit back and relax while we flesh out a memorable photo book for you. You can change anything you like.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to={start} data-testid="hero-start-book-btn" className="btn-primary">
              Start your book <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-6 text-sm text-[#8C8498]">From {inr(1499)} · delivered quick</p>
        </div>
        <motion.div
          initial={{ opacity: 0, rotate: -2, y: 30 }}
          animate={{ opacity: 1, rotate: -1.5, y: 0 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="relative lg:col-span-7"
        >
          <div className="page-shadow overflow-hidden rounded-sm bg-white p-3 sm:p-4">
            <img src={IMG.hero} alt="Open photo book" className="aspect-[4/3] w-full object-cover" />
          </div>
          <motion.div
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
            className="page-shadow absolute -bottom-8 -left-2 hidden w-56 rotate-3 bg-white p-2.5 sm:block"
          >
            <img src={IMG.wedding} alt="Wedding" className="aspect-square w-full object-cover" />
            <p className="mt-2 text-center font-serif italic text-[#5E5868]">"Seven steps, one forever"</p>
          </motion.div>
        </motion.div>
      </section>

      <section id="sample" data-testid="sample-book-section" className="dark-mesh text-[#FAF8F5]">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="overline !text-[#9DB8FF]">Sample books · {SAMPLE_BOOKS[sample].meta}</p>
              <h2 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">Flip through a finished book before you make one.</h2>
            </div>
            <p className="text-sm leading-relaxed text-[#FAF8F5]/60 lg:col-span-5" data-testid="sample-book-blurb">
              {SAMPLE_BOOKS[sample].blurb} Tap a page, swipe, or use the arrows.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-2" role="tablist" data-testid="sample-book-tabs">
            {Object.entries(SAMPLE_BOOKS).map(([k, b]) => (
              <button key={k} type="button" role="tab" aria-selected={sample === k} data-testid={`sample-book-tab-${k}`} onClick={() => setSample(k)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${sample === k ? "border-[#9DB8FF] bg-[#9DB8FF]/15 text-[#FAF8F5]" : "border-[#FAF8F5]/15 text-[#FAF8F5]/60 hover:border-[#FAF8F5]/40 hover:text-[#FAF8F5]"}`}>
                {b.label}
              </button>
            ))}
          </div>
          <div className="mt-10 px-2 sm:px-6 lg:mt-14">
            <SampleBook book={sample} />
          </div>
          <p className="mt-8 text-center text-xs text-[#FAF8F5]/40">Sample photos via Unsplash. Your book is made only from your photos.</p>
        </div>
      </section>

      <section className="border-y border-[#E8E2D9] bg-[#F5F0E8]/60">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <h2 className="max-w-xl font-serif text-3xl tracking-tight sm:text-4xl">Just pick your best memories, sit back and relax while we make it perfect.</h2>
          <p className="overline mt-3">How it works</p>
          <div className="mt-12 grid gap-px overflow-hidden rounded-lg bg-[#E8E2D9] sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ icon: I, t, d }, i) => (
              <div key={t} className={`${["tint-rose","tint-honey","tint-mint","tint-lilac"][i % 4]} p-7`} data-testid={`how-step-${i}`}>
                <I className="h-6 w-6 text-[#3B5BDB]" strokeWidth={1.5} />
                <h3 className="mt-5 text-lg font-semibold tracking-tight">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#5E5868]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <p className="overline">Made for</p>
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {OCCASIONS.map(([t, src], i) => (
            <div key={t} className={`group relative overflow-hidden rounded-sm ${i % 2 ? "lg:mt-10" : ""}`}>
              <img src={src} alt={t} className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <p className="absolute bottom-4 left-4 font-serif text-xl text-white sm:text-2xl">{t}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="caricature" className="mx-auto max-w-7xl px-5 pb-20 sm:px-8" data-testid="caricature-section">
        <div className="grid items-start gap-10 overflow-hidden rounded-2xl bg-gradient-to-br from-[#E9EEFF] via-[#F3F0FA] to-[#FCE7F1] p-8 shadow-[0_8px_30px_rgba(38,35,46,0.06)] sm:p-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="overline !text-[#3B5BDB]">New · Caricature posters</p>
            <h2 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">One photo, ten quirks, one unforgettable poster.</h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#5E5868] sm:text-base">
              Tell us they love cats, never skip leg day and quote Game of Thrones — our artist draws them as a modern big-head caricature: crisp black-and-white ink with just a pop of colour, or go Bollywood-poster or comic-book. One person, a couple, the whole family — or just your pet. Get it as a quick digital copy or a printed A3 poster.
            </p>
            <Link to={user ? "/caricature" : "/register?next=/caricature"} data-testid="landing-caricature-cta" className="btn-primary mt-7">Make a Caricature <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <Tabs defaultValue="samples" className="lg:col-span-7">
            <TabsList className="h-auto rounded-full bg-[#E8E2D9]/70 p-1">
              <TabsTrigger value="samples" data-testid="caricature-tab-samples" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white">Samples</TabsTrigger>
              <TabsTrigger value="pricing" data-testid="caricature-tab-pricing" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white">Pricing</TabsTrigger>
            </TabsList>
            <TabsContent value="samples" data-testid="caricature-samples-panel" className="mt-6">
              <div className="grid grid-cols-3 gap-3 sm:gap-5">
                {CARICATURE_SAMPLES.map(([src, n, q], i) => (
                  <figure key={n} className={`page-shadow bg-white p-1.5 sm:p-2 ${i === 1 ? "sm:-mt-4" : ""}`} data-testid={`caricature-sample-${i}`}>
                    <img src={src} alt={`Caricature of ${n}`} loading="lazy" className="aspect-[2/3] w-full object-cover" />
                    <figcaption className="mt-1.5 truncate text-center text-[10px] text-[#8C8498] sm:text-xs">{q}</figcaption>
                  </figure>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="pricing" data-testid="caricature-pricing-panel" className="mt-6">
              <CaricaturePlans compact />
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-[#8C8498]">GST included · one portrait per upload, free to preview before you buy.</p>
                <Link to="/pricing?tab=caricatures" data-testid="caricature-full-pricing-link" className="text-sm font-medium text-[#3B5BDB] hover:underline">Full pricing details →</Link>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section id="sizes" className="mx-auto max-w-7xl px-5 pb-20 sm:px-8" data-testid="landing-pricing-strip">
        <div className="flex flex-col gap-6 border-y border-[#E8E2D9] py-10 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="overline">Sizes & pricing</p>
            <h2 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">Three shapes. Honest prices.</h2>
            <p className="mt-2 text-sm text-[#5E5868]">Square, Portrait or Landscape — from {inr(1499)} with 20 pages, AI curation and a hard cover included.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="landing-book-pricing-btn" className="btn-primary">Photo book pricing <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/pricing?tab=caricatures" data-testid="landing-caricature-pricing-btn" className="btn-ghost">Caricature pricing</Link>
          </div>
        </div>
      </section>

      <section className="dark-mesh text-[#FAF8F5]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-3">
          {[
            [Printer, "Printed in India", "300 DPI, print-ready files sent to an India-local print partner."],
            [Truck, "Quick Delivery", "Two weeks is our target, not a promise — we'll keep you posted at every step."],
            [ShieldCheck, "Your photos, private", "Uploads are automatically deleted 30 days after your book is delivered."],
          ].map(([I, t, d]) => (
            <div key={t}>
              <I className="h-6 w-6 text-[#9DB8FF]" strokeWidth={1.5} />
              <h3 className="mt-4 font-serif text-2xl">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#FAF8F5]/70">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
